import React, { useState, useEffect, useRef } from 'react'

import {
  Modal,
  Icon,
  Button,
  ModalHeader,
  ModalContent,
  Textfield,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@clientbase/clientbase-library'
import { Box, Stack, Typography, IconButton, Tooltip } from '@mui/material'
import Papa from 'papaparse'

import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import ModalRecurrenceDetail from 'components/Modal/ModalRecurrenceDetail'
import Status, { StatusKeys } from 'components/Status'

import { formatMoney, normalizeString, formatPhone, formatPercent } from 'utils'

import { theme } from 'styles/theme'

import * as S from './CSVModal.styles'

interface CSVModalProps {
  open: boolean
  onClose: () => void
  csvUrl: string
  fullScreen?: boolean
}

interface CSVData {
  [key: string]: string | number
}

type SortDirection = 'asc' | 'desc' | null

interface FilterState {
  [key: string]: string
}

const CSVModal: React.FC<CSVModalProps> = ({
  open,
  onClose,
  csvUrl,
  fullScreen = false
}) => {
  const [csvData, setCsvData] = useState<CSVData[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [filters, setFilters] = useState<FilterState>({})
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const [isOpenChargeDetail, setIsOpenChargeDetail] = useState(false)
  const [selectedChargeUuid, setSelectedChargeUuid] = useState<string>('')
  const [isOpenRecurrenceDetail, setIsOpenRecurrenceDetail] = useState(false)
  const [selectedRecurrenceUuid, setSelectedRecurrenceUuid] =
    useState<string>('')

  const mainScrollRef = useRef<HTMLDivElement | null>(null)
  const mainTableRef = useRef<HTMLTableElement | null>(null)
  const focusRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const id = requestAnimationFrame(() => {
      focusRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [open])

  // Função para detectar links genéricos (qualquer URL que comece com https://)

  // Detectar e extrair UUID de links de fatura (https://fatura.subdominio.com.br/uuid)
  const extractInvoiceUuid = (url: string): string | null => {
    const match = url.match(
      /^https:\/\/fatura\.[^/]+\.com\.br\/([0-9a-fA-F-]{36})/
    )
    return match ? match[1] : null
  }

  const isHttpLink = (value: string): boolean => {
    return typeof value === 'string' && value.startsWith('https://')
  }

  // Detectar links de cliente: https://base.subdominio.com.br/clientes/uuid
  const isCustomerLink = (url: string): boolean =>
    /^https:\/\/base\.[^/]+\.com\.br\/clientes\/[0-9a-fA-F-]{36}/.test(url)

  // Detectar links de produto: https://base.subdominio.com.br/planos/uuid
  const isProductLink = (url: string): boolean =>
    /^https:\/\/base\.[^/]+\.com\.br\/planos\/[0-9a-fA-F-]{36}/.test(url)

  const isUuid = (value: string): boolean =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value
    )

  // Coluna cujo header indica product_uuid (ex.: "Product uuid", "product_uuid")
  const isProductUuidHeader = (header: string): boolean =>
    /product[_\s-]?uuid/i.test(header)

  const buildProductUrlFromUuid = (uuid: string): string | null => {
    if (typeof window === 'undefined') return null
    return `${window.location.origin}/planos/${uuid}`
  }

  // Detectar e extrair UUID de links de recorrência (https://base.subdominio.com.br/cobrancas?tab=recorrencias&recurrence_uuid=uuid)
  const extractRecurrenceUuid = (url: string): string | null => {
    const match = url.match(/recurrence_uuid=([0-9a-fA-F-]{36})/)
    return match ? match[1] : null
  }
  // Função para verificar se uma coluna contém links (e não deve aparecer nos filtros)
  // Colunas especiais de ação (fatura/cliente/recorrência/produto)
  const isSpecialActionColumn = (header: string): boolean => {
    if (csvData.length === 0) return false
    const nonEmptyValues = csvData
      .map((row) => row[header])
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')

    if (nonEmptyValues.length === 0) return false

    // Coluna explicitamente de product_uuid (valores são UUIDs puros)
    if (isProductUuidHeader(header)) {
      const uuidCount = nonEmptyValues.filter((v) =>
        isUuid(String(v).trim())
      ).length
      if (uuidCount > nonEmptyValues.length * 0.5) return true
    }

    let specialCount = 0
    for (const v of nonEmptyValues) {
      const s = String(v)
      if (
        extractInvoiceUuid(s) ||
        isCustomerLink(s) ||
        isProductLink(s) ||
        extractRecurrenceUuid(s)
      )
        specialCount += 1
    }
    return specialCount > nonEmptyValues.length * 0.5
  }

  const isLinkColumn = (header: string): boolean => {
    if (csvData.length === 0) return false

    // Verificar se a maioria dos valores não vazios da coluna são links
    const nonEmptyValues = csvData
      .map((row) => row[header])
      .filter(
        (value) =>
          value !== null && value !== undefined && String(value).trim() !== ''
      )

    if (nonEmptyValues.length === 0) return false

    const linkCount = nonEmptyValues.filter((value) =>
      isHttpLink(String(value))
    ).length

    // Se mais de 50% dos valores são links, considerar como coluna de link
    return linkCount > nonEmptyValues.length * 0.5
  }

  // Função para verificar se um valor é numérico (excluindo datas e textos)
  const isNumeric = (value: string | number): boolean => {
    if (typeof value === 'number') return true
    if (typeof value === 'string') {
      const stringValue = value.toString().trim()

      // Excluir campos vazios
      if (!stringValue) return false

      // Excluir UUIDs (formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
      if (
        /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
          stringValue
        )
      )
        return false

      // Excluir datas (formatos comuns: DD/MM/YYYY, YYYY-MM-DD, etc.)
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(stringValue)) return false
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(stringValue)) return false
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(stringValue)) return false

      // Excluir textos que claramente não são números
      if (/^[a-zA-Z\s]+$/.test(stringValue)) return false

      // Verificar se é um número válido (incluindo decimais com vírgula)
      const cleanValue = stringValue.replace(/[^\d.,-]/g, '').replace(',', '.')
      const numValue = parseFloat(cleanValue)
      return !isNaN(numValue) && isFinite(numValue) && cleanValue.length > 0
    }
    return false
  }

  // Função para mapear status do CSV para StatusKeys do componente Status
  const mapStatusToStatusKey = (status: string): StatusKeys | null => {
    const statusLower = status.toLowerCase()

    switch (statusLower) {
      case 'pendente':
      case 'pending':
        return 'pending'
      case 'aberta':
        return 'open_payment'
      case 'paga':
        return 'paid'
      case 'expirada':
        return 'expired'
      case 'cancelada':
        return 'cancelled'
      case 'falhada':
        return 'cancelled'
      case 'suspensa':
        return 'suspended'
      case 'ativa':
        return 'active'
      case 'finalizada':
        return 'finished'
      case 'confirmada':
        return 'confirmed'
      case 'emitida':
        return 'issued'

      case 'arquivado':
        return 'archived'
      case 'bloqueado':
        return 'blocked'
      case 'deletado':
        return 'deleted'
      case 'com erro':
        return 'with_error'
      // Status em inglês
      case 'open_payment':
        return 'open_payment'
      case 'paid':
        return 'paid'
      case 'created_payment':
        return 'created_payment'
      case 'no_payment':
        return 'no_payment'
      case 'expiring':
        return 'expiring'
      case 'cancelled':
      case 'cancelling':
        return 'cancelled'
      case 'expired':
        return 'expired'
      case 'suspended':
        return 'suspended'
      case 'active':
        return 'active'
      case 'finished':
        return 'finished'
      case 'issued':
        return 'issued'
      case 'confirmed':
        return 'confirmed'
      case 'succeeded':
        return 'succeeded'
      case 'failed':
        return 'failed'
      case 'archived':
        return 'archived'
      case 'blocked':
        return 'blocked'
      case 'deleted':
        return 'deleted'
      case 'with_error':
        return 'with_error'
      case 'processing':
        return 'processing'
      default:
        return null
    }
  }

  // Função para verificar se é uma coluna de status
  const isStatusColumn = (header: string): boolean => {
    const headerLower = header.toLowerCase()
    return headerLower === 'status' || headerLower.includes('status')
  }

  // Coluna de telefone: detecta por nome
  const isPhoneColumn = (header: string): boolean => {
    const headerLower = header.toLowerCase()
    return headerLower.includes('telefone') || headerLower.includes('phone')
  }

  const isCurrencyColumn = (header: string): boolean => /R\$/i.test(header)

  const isPercentColumn = (header: string): boolean => /%/.test(header)

  const isDateColumn = (header: string): boolean => /data/i.test(header)

  // Função para determinar o tipo da coluna para estilização
  const getColumnType = (header: string): string => {
    if (isLinkColumn(header)) return 'link'
    if (isStatusColumn(header)) return 'status'
    if (isPhoneColumn(header)) return 'phone'
    if (isCurrencyColumn(header)) return 'currency'
    if (isPercentColumn(header)) return 'percent'
    if (isDateColumn(header)) return 'date'

    // Detectar colunas curtas (IDs, códigos, etc.)
    const headerLower = header.toLowerCase()
    if (
      headerLower.includes('id') ||
      headerLower.includes('código') ||
      headerLower.includes('code') ||
      headerLower.length <= 5
    ) {
      return 'short'
    }

    // Detectar colunas longas (descrições, observações, etc.)
    if (
      headerLower.includes('descrição') ||
      headerLower.includes('description') ||
      headerLower.includes('observação') ||
      headerLower.includes('observation') ||
      headerLower.includes('comentário') ||
      headerLower.includes('comment') ||
      headerLower.includes('detalhes') ||
      headerLower.includes('details')
    ) {
      return 'long'
    }

    return 'default'
  }

  // Função para renderizar célula com links especiais e formatação
  const renderCellContent = (
    value: string | number,
    header: string,
    rowData?: CSVData
  ): React.ReactNode => {
    const stringValue = String(value || '')

    // Telefone formatado
    if (isPhoneColumn(header) && stringValue.trim()) {
      return formatPhone(stringValue)
    }

    if (isPercentColumn(header) && stringValue.trim()) {
      return `${formatPercent(stringValue)}%`
    }

    if (isHttpLink(stringValue)) {
      const invoiceUuid = extractInvoiceUuid(stringValue)
      if (invoiceUuid) {
        return (
          <Tooltip title="Visualizar cobrança">
            <IconButton
              aria-label={`Abrir detalhes da cobrança`}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedChargeUuid(invoiceUuid)
                setIsOpenChargeDetail(true)
              }}
              size="small"
              sx={{ width: 32, height: 32, borderRadius: '8px' }}
            >
              <Icon icon="receipt" />
            </IconButton>
          </Tooltip>
        )
      }
      if (isCustomerLink(stringValue)) {
        return (
          <Tooltip title="Visualizar cliente">
            <IconButton
              aria-label={`Abrir cliente`}
              onClick={(e) => {
                e.stopPropagation()
                window.open(stringValue, '_blank', 'noopener,noreferrer')
              }}
              size="small"
              sx={{ width: 32, height: 32, borderRadius: '8px' }}
            >
              <Icon icon="faUser" />
            </IconButton>
          </Tooltip>
        )
      }
      return (
        <IconButton
          aria-label={`Abrir ${header}`}
          onClick={(e) => {
            e.stopPropagation()
            window.open(stringValue, '_blank', 'noopener,noreferrer')
          }}
          size="small"
          sx={{ width: 32, height: 32, borderRadius: '8px' }}
        >
          <Icon icon="open" />
        </IconButton>
      )
    }

    // Verificar se é uma coluna de status e renderizar com componente Status
    if (isStatusColumn(header) && stringValue.trim()) {
      const statusKey = mapStatusToStatusKey(stringValue)

      if (statusKey) {
        let dueDate: string | undefined

        // Se temos os dados da linha, procurar pela data de vencimento
        if (rowData) {
          const dueDateKeys = Object.keys(rowData).filter(
            (key) =>
              key.toLowerCase().includes('vencimento') ||
              key.toLowerCase().includes('due') ||
              key.toLowerCase().includes('data de vencimento')
          )
          if (dueDateKeys.length > 0) {
            const rawDueDate = String(rowData[dueDateKeys[0]])
            // Converter data do formato DD/MM/YYYY para YYYY-MM-DD se necessário
            if (rawDueDate.includes('/')) {
              const [day, month, year] = rawDueDate.split('/')
              dueDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
                2,
                '0'
              )}`
            } else {
              dueDate = rawDueDate
            }
          }
        }

        return <Status status={statusKey} dueDate={dueDate} />
      }

      // Se não conseguir mapear, retorna o texto original
      return stringValue
    }

    // Verificar se é um campo monetário e formatar
    if (isCurrencyColumn(header) && isNumeric(value)) {
      const numValue =
        typeof value === 'number'
          ? value
          : parseFloat(
              String(value)
                .replace(/[^\d.,-]/g, '')
                .replace(',', '.')
            )
      return formatMoney(numValue)
    }

    return stringValue
  }

  const fetchCSVData = async (url: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Erro ao carregar o arquivo CSV')
      }

      const csvText = await response.text()

      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn('Erros ao processar CSV:', results.errors)
          }

          const data = results.data as CSVData[]
          setCsvData(data)

          if (data.length > 0) {
            setHeaders(Object.keys(data[0]))
          }

          setLoading(false)
        },
        error: () => {
          setError('Erro ao processar o arquivo CSV')
          setLoading(false)
        }
      })
    } catch (err) {
      setError('Erro ao carregar o arquivo CSV')
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && csvUrl) {
      fetchCSVData(csvUrl)
    }
  }, [open, csvUrl])

  const handleSort = (column: string) => {
    if (isLinkColumn(column)) return

    let newDirection: SortDirection = 'asc'

    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        newDirection = 'desc'
      } else if (sortDirection === 'desc') {
        newDirection = null
      } else {
        newDirection = 'asc'
      }
    }

    setSortColumn(newDirection ? column : null)
    setSortDirection(newDirection)
  }

  // Função para filtrar dados (case-insensitive e sem acentos)
  const getFilteredData = () => {
    return csvData.filter((row) => {
      return headers.every((header) => {
        const filterValue = filters[header]
        if (!filterValue) return true

        const cellValue = String(row[header] || '')
        const normCell = normalizeString(cellValue.toLowerCase())
        const normFilter = normalizeString(filterValue.toLowerCase())
        return normCell.includes(normFilter)
      })
    })
  }

  // Função para ordenar dados filtrados
  const getSortedAndFilteredData = () => {
    const filteredData = getFilteredData()

    if (!sortColumn || !sortDirection) {
      return filteredData
    }

    // Helpers para detectar e comparar datas
    const parseDate = (value?: string | number) => {
      if (value == null) return NaN
      const str = String(value).trim()
      if (!str) return NaN
      // DD/MM/YYYY
      const m1 = str.match(/^([0-3]?\d)\/([0-1]?\d)\/(\d{4})$/)
      if (m1) {
        const [, d, m, y] = m1
        return new Date(Number(y), Number(m) - 1, Number(d)).getTime()
      }
      // YYYY-MM-DD
      const m2 = str.match(/^(\d{4})-([0-1]?\d)-([0-3]?\d)$/)
      if (m2) {
        const [, y, m, d] = m2
        return new Date(Number(y), Number(m) - 1, Number(d)).getTime()
      }
      // DD-MM-YYYY
      const m3 = str.match(/^([0-3]?\d)-([0-1]?\d)-(\d{4})$/)
      if (m3) {
        const [, d, m, y] = m3
        return new Date(Number(y), Number(m) - 1, Number(d)).getTime()
      }
      // ISO ou outros formatos parsáveis
      const t = Date.parse(str)
      return isNaN(t) ? NaN : t
    }

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn]
      const bValue = b[sortColumn]

      const aDate = parseDate(aValue as any)
      const bDate = parseDate(bValue as any)

      let comparison = 0
      const headerIsDate = isDateColumn(sortColumn)

      if (headerIsDate) {
        const aTime = isNaN(aDate) ? Number.POSITIVE_INFINITY : aDate
        const bTime = isNaN(bDate) ? Number.POSITIVE_INFINITY : bDate
        comparison = aTime - bTime
      } else if (!isNaN(aDate) && !isNaN(bDate)) {
        // Comparar como datas
        comparison = aDate - bDate
      } else {
        // Converter para string para comparação
        const aStr = String(aValue || '').toLowerCase()
        const bStr = String(bValue || '').toLowerCase()

        // Tentar converter para número se possível
        const aNum = parseFloat(aStr)
        const bNum = parseFloat(bStr)

        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum
        } else {
          comparison = aStr.localeCompare(bStr)
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }

  // Somatório apenas das colunas monetárias (headers contendo "R$")
  const calculateColumnSums = () => {
    const filteredData = getFilteredData()
    const sums: { [key: string]: string } = {}

    headers.forEach((header) => {
      if (!isCurrencyColumn(header)) {
        sums[header] = ''
        return
      }

      const values = filteredData
        .map((row) => row[header])
        .filter(
          (value) =>
            value !== null && value !== undefined && String(value).trim() !== ''
        )

      if (values.length === 0) {
        sums[header] = ''
        return
      }

      const sum = values.reduce((acc: number, value) => {
        const numValue =
          typeof value === 'number'
            ? value
            : parseFloat(
                String(value)
                  .replace(/[^\d.,-]/g, '')
                  .replace(',', '.')
              )
        return acc + (isNaN(numValue) ? 0 : numValue)
      }, 0)

      sums[header] = formatMoney(sum)
    })

    return sums
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = csvUrl
    link.download = 'dados.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFilterChange = (header: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [header]: value
    }))
  }

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFiltersAnchorEl(event.currentTarget)
  }

  const handleCloseFilters = () => {
    setFiltersAnchorEl(null)
  }

  const handleClearFilters = () => {
    setFilters({})
  }

  const handleDeleteFilter = (header: string) => {
    setFilters((prev) => ({
      ...prev,
      [header]: ''
    }))
  }

  const handleClose = () => {
    setCsvData([])
    setHeaders([])
    setError(null)
    setSortColumn(null)
    setSortDirection(null)
    setFilters({})
    setIsOpenChargeDetail(false)
    setSelectedChargeUuid('')
    setIsOpenRecurrenceDetail(false)
    setSelectedRecurrenceUuid('')
    onClose()
  }

  const columnSums = calculateColumnSums()
  const filteredData = getFilteredData()

  const actionColumns = headers.filter((h) => isSpecialActionColumn(h))
  const visibleHeaders = headers.filter((h) => !isSpecialActionColumn(h))

  const anyRowHasTwoActions = getSortedAndFilteredData().some((row) => {
    const invoiceUuid = actionColumns
      .map((h) => extractInvoiceUuid(String(row[h] || '')))
      .find((v) => !!v)
    const recurrenceUuid = actionColumns
      .map((h) => extractRecurrenceUuid(String(row[h] || '')))
      .find((v) => !!v)
    const hasCustomer = actionColumns
      .map((h) => String(row[h] || ''))
      .some((v) => isCustomerLink(v))
    return (
      (!!invoiceUuid && hasCustomer) ||
      (!!invoiceUuid && !!recurrenceUuid) ||
      (!!recurrenceUuid && hasCustomer)
    )
  })
  const actionsWidth = anyRowHasTwoActions ? 75 : 45
  const actionsIconSize = actionsWidth === 45 ? 26 : 28

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      width={fullScreen ? '100vw' : '90vw'}
      height={fullScreen ? '100vh' : '90vh'}
    >
      <div
        ref={focusRef}
        tabIndex={-1}
        style={{
          width: 0,
          height: 0,
          padding: 0,
          margin: 0,
          border: 0,
          outline: 'none'
        }}
        aria-hidden={true}
      />

      <ModalHeader onClose={handleClose}>
        <Stack
          justifyContent="space-between"
          direction="row"
          alignItems="center"
          mr={2}
          width="100%"
        >
          <Typography variant="mdBold">
            {filteredData.length} resultados
          </Typography>
          {!loading && !error && csvData.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variantButton="neutral"
                buttonSize="sm"
                startIcon="filterAlt"
                onClick={handleOpenFilters}
              >
                Filtros
              </Button>
              <Button
                variantButton="primary"
                buttonSize="sm"
                startIcon="download"
                onClick={handleDownload}
              >
                Baixar CSV
              </Button>
            </Box>
          )}
        </Stack>
      </ModalHeader>

      <ModalContent
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}
      >
        {loading && (
          <S.LoadingContainer>
            <S.LoadingText>Carregando dados do CSV...</S.LoadingText>
          </S.LoadingContainer>
        )}

        {error && (
          <S.ErrorContainer>
            <S.ErrorText>{error}</S.ErrorText>
          </S.ErrorContainer>
        )}

        {!loading && !error && csvData.length > 0 && (
          <S.TableContainer>
            <S.TableWrapper ref={mainScrollRef}>
              <S.Table ref={mainTableRef}>
                <S.TableHead>
                  <S.TableRow>
                    {visibleHeaders.map((header, index) => {
                      const sortable = !isLinkColumn(header)
                      const columnType = getColumnType(header)
                      return (
                        <S.TableHeaderCell
                          key={index}
                          $columnType={columnType}
                          onClick={
                            sortable ? () => handleSort(header) : undefined
                          }
                          style={{
                            cursor: sortable ? 'pointer' : 'default',
                            userSelect: 'none'
                          }}
                          title={sortable ? '' : 'Coluna não ordenável'}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {header}
                            {sortable &&
                              sortColumn === header &&
                              sortDirection && (
                                <Icon
                                  icon={
                                    sortDirection === 'asc'
                                      ? 'arrowUpward'
                                      : 'arrowDownward'
                                  }
                                  style={{
                                    fontSize: '16px',
                                    opacity: 1,
                                    transition: 'opacity 0.2s'
                                  }}
                                />
                              )}
                          </div>
                        </S.TableHeaderCell>
                      )
                    })}
                    {actionColumns.length > 0 && (
                      <S.ActionsHeaderCell $actionsWidth={actionsWidth}>
                        Ações
                      </S.ActionsHeaderCell>
                    )}
                  </S.TableRow>
                </S.TableHead>
                <S.TableBody>
                  {getSortedAndFilteredData().map((row, rowIndex) => (
                    <S.TableRow key={rowIndex}>
                      {visibleHeaders.map((header, cellIndex) => (
                        <S.TableCell key={cellIndex}>
                          {renderCellContent(row[header], header, row)}
                        </S.TableCell>
                      ))}

                      {actionColumns.length > 0 && (
                        <S.ActionsCell $actionsWidth={actionsWidth}>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: actionsWidth === 45 ? 0 : 1,
                              justifyContent: 'center'
                            }}
                          >
                            {/* Ação: Detalhes da cobrança via UUID */}
                            {(() => {
                              const invoiceUuid = actionColumns
                                .map((h) =>
                                  extractInvoiceUuid(String(row[h] || ''))
                                )
                                .find((v) => !!v)
                              return invoiceUuid ? (
                                <Tooltip title="Visualizar cobrança" arrow>
                                  <IconButton
                                    aria-label={`Abrir detalhes da cobrança`}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedChargeUuid(
                                        invoiceUuid as string
                                      )
                                      setIsOpenChargeDetail(true)
                                    }}
                                    size="small"
                                    sx={{
                                      width: actionsIconSize,
                                      height: actionsIconSize,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <Icon icon="receipt" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            })()}

                            {/* Ação: Detalhes do cliente (abre em nova aba) */}
                            {(() => {
                              const customerUrl = actionColumns
                                .map((h) => String(row[h] || ''))
                                .find((v) => isCustomerLink(v))
                              return customerUrl ? (
                                <Tooltip title="Visualizar cliente" arrow>
                                  <IconButton
                                    aria-label={`Abrir cliente`}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      window.open(
                                        customerUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }}
                                    size="small"
                                    sx={{
                                      width: actionsIconSize,
                                      height: actionsIconSize,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <Icon icon="faUser" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            })()}

                            {/* Ação: Detalhes da recorrência via UUID */}
                            {(() => {
                              const recurrenceUuid = actionColumns
                                .map((h) =>
                                  extractRecurrenceUuid(String(row[h] || ''))
                                )
                                .find((v) => !!v)
                              return recurrenceUuid ? (
                                <Tooltip title="Visualizar recorrência" arrow>
                                  <IconButton
                                    aria-label={`Abrir detalhes da recorrência`}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedRecurrenceUuid(
                                        recurrenceUuid as string
                                      )
                                      setIsOpenRecurrenceDetail(true)
                                    }}
                                    size="small"
                                    sx={{
                                      width: actionsIconSize,
                                      height: actionsIconSize,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <Icon icon="cached" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            })()}

                            {/* Ação: Detalhes do produto (abre em nova aba) */}
                            {(() => {
                              const productUrlFromLink = actionColumns
                                .map((h) => String(row[h] || ''))
                                .find((v) => isProductLink(v))

                              let productUrl: string | null =
                                productUrlFromLink || null

                              if (!productUrl) {
                                const productUuid = actionColumns
                                  .filter((h) => isProductUuidHeader(h))
                                  .map((h) => String(row[h] || '').trim())
                                  .find((v) => isUuid(v))
                                if (productUuid) {
                                  productUrl =
                                    buildProductUrlFromUuid(productUuid)
                                }
                              }

                              const resolvedProductUrl = productUrl
                              return resolvedProductUrl ? (
                                <Tooltip title="Visualizar produto" arrow>
                                  <IconButton
                                    aria-label={`Abrir produto`}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      window.open(
                                        resolvedProductUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }}
                                    size="small"
                                    sx={{
                                      width: actionsIconSize,
                                      height: actionsIconSize,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <Icon icon="storefront" />
                                  </IconButton>
                                </Tooltip>
                              ) : null
                            })()}
                          </Box>
                        </S.ActionsCell>
                      )}
                    </S.TableRow>
                  ))}
                </S.TableBody>
                <S.TableFoot>
                  <S.SummaryRow>
                    {visibleHeaders.map((header, cellIndex) => (
                      <S.TableCell
                        key={cellIndex}
                        style={{
                          fontWeight: 'bold',
                          color: theme.palette.neutral[200],
                          backgroundColor: 'transparent'
                        }}
                      >
                        {columnSums[header]}
                      </S.TableCell>
                    ))}
                    {actionColumns.length > 0 && (
                      <S.ActionsCell $actionsWidth={actionsWidth} />
                    )}
                  </S.SummaryRow>
                </S.TableFoot>
              </S.Table>
            </S.TableWrapper>
          </S.TableContainer>
        )}

        {!loading && !error && csvData.length === 0 && (
          <S.EmptyContainer>
            <S.EmptyText>Nenhum dado encontrado no arquivo CSV</S.EmptyText>
          </S.EmptyContainer>
        )}

        {/* Popover de filtros */}
        <Popover
          open={Boolean(filtersAnchorEl)}
          anchorEl={filtersAnchorEl}
          onClose={handleCloseFilters}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <S.PopoverContainer>
            <S.ScrollableContent>
              {headers
                .filter((header) => !isLinkColumn(header))
                .map((header) => (
                  <Accordion key={header} defaultExpanded={!!filters[header]}>
                    <AccordionSummary>
                      <S.AccordionSummaryContent>
                        &#8288;{header}
                        {!!filters[header] && (
                          <Icon
                            icon="cancel"
                            onClick={() => handleDeleteFilter(header)}
                            width="20px"
                            sx={{ mr: 1 }}
                          />
                        )}
                      </S.AccordionSummaryContent>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Textfield
                        value={filters[header] || ''}
                        onChange={(e) =>
                          handleFilterChange(header, e.target.value)
                        }
                        placeholder={`Filtrar por ${header}`}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
            </S.ScrollableContent>

            <S.FixedButtonsContainer>
              <Button
                variantButton="neutral"
                onClick={handleClearFilters}
                fullWidth
              >
                Limpar
              </Button>
            </S.FixedButtonsContainer>
          </S.PopoverContainer>
        </Popover>
      </ModalContent>

      {isOpenChargeDetail && (
        <ModalChargeDetail
          uuid={selectedChargeUuid}
          isOpenChargeDetail={isOpenChargeDetail}
          handleCloseChargeDetail={() => setIsOpenChargeDetail(false)}
        />
      )}

      {isOpenRecurrenceDetail && (
        <ModalRecurrenceDetail
          uuid={selectedRecurrenceUuid}
          isOpenModalRecurrenceDetail={isOpenRecurrenceDetail}
          handleCloseDetailRecurrence={() => setIsOpenRecurrenceDetail(false)}
        />
      )}
    </Modal>
  )
}

export default CSVModal
